import Form from "./Form";
import RuleGroup from "./RuleGroup";
import Checkboxes from "./FieldHandlers/Checkboxes";
import SubmitEvent = JQuery.SubmitEvent;

export const FORM_CLASS = 'mhp-form'
const INITIATED_CLASS = FORM_CLASS + '--initiated'
const PAGE_CLASS = FORM_CLASS + '-multipage-page'
const PAGE_ACTIVE_CLASS = PAGE_CLASS + '--active'
const CONDITION_HIDDEN_CLASS = FORM_CLASS + '-condition--hidden'
const CONDITION_BOUNDARY_CLASS = FORM_CLASS + '-condition-boundary'
const BUTTON_DISABLED_CLASS = FORM_CLASS + '-button--disabled'
export const INVALID_CLASS = FORM_CLASS + '--invalid'

export default class FormHandler {
    private readonly multipage: boolean
    private readonly pages: JQuery<HTMLFieldSetElement>

    constructor(
        public readonly form: Form,
        public readonly el: JQuery<HTMLFormElement>
    ) {
        this.pages = el.find<HTMLFieldSetElement>('fieldset.' + PAGE_CLASS)
        this.multipage = this.pages.length > 0
    }

    public get activePage(): JQuery<HTMLFieldSetElement> {
        return this.pages.filter('.' + PAGE_ACTIVE_CLASS)
    }

    protected set activePage(page: JQuery<HTMLFieldSetElement>) {
        this.pages.removeClass(PAGE_ACTIVE_CLASS)
        page.addClass(PAGE_ACTIVE_CLASS)
        this.calculatePageNumbers()
    }

    public static makeFromForm(form: Form): FormHandler[] {
        return jQuery(`form.${FORM_CLASS}[data-key="${form.key}"]`).toArray().map((el: HTMLFormElement) => {
            return new FormHandler(form, jQuery<typeof el>(el))
        })
    }

    init(): void {
        if (this.el.hasClass(INITIATED_CLASS)) {
            return
        }

        if (this.multipage) {
            this.el.find('button.' + FORM_CLASS + '-submit').remove()

            this.pages.first().addClass(PAGE_ACTIVE_CLASS)
            this.pages.last()
                .find('button.' + PAGE_CLASS + '-button-next')
                .attr('type', 'submit')
                .text('Absenden')
                .addClass(PAGE_CLASS + '-submit')

            this.el.find('.' + PAGE_CLASS + '-button-prev').on('click.' + FORM_CLASS, this.previousPage.bind(this))
            this.el.find('.' + PAGE_CLASS + '-button-next').on('click.' + FORM_CLASS, this.nextPage.bind(this))
        }

        this.initConditions()

        this.el.find('input, select, textarea').on(`input.${FORM_CLASS} change.${FORM_CLASS}`, this.maybeMarkButtonDisabled.bind(this))
        this.el.on(`submit.${FORM_CLASS}`, this.submit.bind(this))

        this.initFieldHandlers()

        this.maybeMarkButtonDisabled()
        this.calculatePageNumbers()

        this.el.addClass(INITIATED_CLASS)
    }

    public previousPage() {
        let newPage = this.activePage.prev('.' + PAGE_CLASS)
        while (newPage.hasClass(CONDITION_HIDDEN_CLASS)) {
            newPage = newPage.prev('.' + PAGE_CLASS)
        }
        if (newPage && newPage.length > 0) {
            this.activePage = newPage
        }
    }

    public nextPage() {
        const page = this.activePage
        if (!page.is(':valid')) {
            const invalidVisibleFields = page.find<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input:not(:valid),select:not(:valid),textarea:not(:valid)').filter(':visible')
            if (invalidVisibleFields.length > 0) {
                invalidVisibleFields[0].reportValidity()
            }
            return
        }
        if (page.find('.' + INVALID_CLASS).length > 0) {
            return
        }

        let newPage = page.next('.' + PAGE_CLASS)
        while (newPage.hasClass(CONDITION_HIDDEN_CLASS)) {
            newPage = newPage.next('.' + PAGE_CLASS)
        }
        if (newPage && newPage.length > 0) {
            this.activePage = newPage
        }
    }

    protected initConditions() {
        const items: Map<string, {
            target: JQuery<HTMLElement>,
            ruleGroup: RuleGroup,
        }> = new Map

        this.form.conditions.forEach((ruleGroup, key) => items.set(key, {
            target: this.el.find('#' + key).addClass(CONDITION_BOUNDARY_CLASS),
            ruleGroup,
        }))

        items.forEach(item => this.initCondition(item.target, item.ruleGroup))
    }

    protected initCondition(target: JQuery, ruleGroup: RuleGroup) {
        const elements = this.el.find(ruleGroup.getElementIds().map(id => '#' + id).join(','))
        const required = target.find('input[required], select[required], textarea[required]')
            .not(target.find('.' + CONDITION_BOUNDARY_CLASS + ' input[required], .' + CONDITION_BOUNDARY_CLASS + ' select[required], .' + CONDITION_BOUNDARY_CLASS + ' textarea[required]'))

        const handleChange = () => {
            if (ruleGroup.matches(this.el)) {
                target.removeClass(CONDITION_HIDDEN_CLASS)
                required.attr('required', 'required')
            } else {
                target.addClass(CONDITION_HIDDEN_CLASS)
                required.removeAttr('required')
            }

            this.calculatePageNumbers()
        }
        elements.on(`input.${FORM_CLASS} change.${FORM_CLASS}`, handleChange)
        handleChange()
    }


    protected initFieldHandlers() {
        (new Checkboxes(this)).init()
    }

    protected maybeMarkButtonDisabled() {
        let validation: JQuery<HTMLFormElement | HTMLFieldSetElement> = this.el
        let button: JQuery<HTMLButtonElement> = this.el.find<HTMLButtonElement>('button.' + FORM_CLASS + '-submit')
        if (this.multipage) {
            validation = this.activePage
            button = validation.find<HTMLButtonElement>('.' + PAGE_CLASS + '-button-next')
        }

        if (validation.is(':valid') && validation.find('.' + INVALID_CLASS).length < 1) {
            button.removeClass(BUTTON_DISABLED_CLASS)
        } else {
            button.addClass(BUTTON_DISABLED_CLASS)
        }
    }

    protected calculatePageNumbers(): void {
        if (!this.multipage) {
            return
        }

        this.el.attr('data-current-page', this.pages.not('.' + CONDITION_HIDDEN_CLASS).index(this.activePage) + 1)
        this.el.attr('data-max-pages', this.pages.not('.' + CONDITION_HIDDEN_CLASS).length)
    }

    protected submit(e: SubmitEvent) {
        if (this.multipage && !this.pages.last().hasClass(PAGE_ACTIVE_CLASS)) {
            e.preventDefault()
            return this.nextPage()
        }
    }
}
